/**
 * Date and timestamp formatters for the public tracking page. All
 * SSR-safe (no React, no DOM) so they can be imported from server
 * components as well as client components.
 *
 * Operator-side error mapping lives separately; the recipient-facing
 * `mapPublicTrackingError` is in `./api`.
 */

export function formatScanTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a YYYY-MM-DD date-only string without timezone shifting (parsing
 * a bare ISO date as UTC and rendering in a negative offset turns the
 * date into the previous day).
 */
export function formatDateOnly(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Parse either a bare `YYYY-MM-DD` (wall-clock, no TZ shift) or a full ISO
 * timestamp (instant) into a Date. Returns null on invalid input.
 */
function parseDateOrTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Jun 7" — short month + day, no year. Accepts a date or a timestamp. */
export function formatDayShort(
  value: string | null | undefined,
): string | null {
  const d = parseDateOrTimestamp(value);
  if (!d) return null;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

/** "Tue, Jun 10" — weekday + short month + day. Accepts a date or a timestamp. */
export function formatDateWeekday(
  value: string | null | undefined,
): string | null {
  const d = parseDateOrTimestamp(value);
  if (!d) return null;
  return d.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function ordinalSuffix(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/** "June 9th, 2026" — long month + ordinal day + year. */
export function formatDateLong(
  value: string | null | undefined,
): string | null {
  const d = parseDateOrTimestamp(value);
  if (!d) return null;
  const month = d.toLocaleDateString([], { month: "long" });
  return `${month} ${d.getDate()}${ordinalSuffix(d.getDate())}, ${d.getFullYear()}`;
}
